'use client'

import { useState, useEffect } from 'react'

interface CountdownTimerProps {
  targetDate: string
}

interface TimeLeft {
  days: number
  hours: number
  minutes: number
  seconds: number
}

function getTimeLeft(target: Date): TimeLeft | null {
  const now = new Date().getTime()
  const diff = target.getTime() - now

  if (diff <= 0) return null

  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  }
}

export function CountdownTimer({ targetDate }: CountdownTimerProps) {
  const target = new Date(targetDate)
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(getTimeLeft(target))
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    const interval = setInterval(() => {
      setTimeLeft(getTimeLeft(target))
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  if (!mounted) {
    return (
      <div className="flex items-center justify-center gap-3 sm:gap-4">
        {['dias', 'horas', 'min', 'seg'].map((label) => (
          <div key={label} className="text-center">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-3 sm:px-5 py-2.5 sm:py-3 min-w-[64px] sm:min-w-[80px]">
              <span className="text-2xl sm:text-3xl font-bold text-white tabular-nums">--</span>
            </div>
            <span className="text-[10px] sm:text-xs text-stone-400 uppercase tracking-wider mt-1.5 block">
              {label}
            </span>
          </div>
        ))}
      </div>
    )
  }

  if (!timeLeft) {
    const now = new Date()
    const eventEnd = new Date(target.getTime() + 10 * 60 * 60 * 1000)
    if (now < eventEnd) {
      return (
        <div className="text-center">
          <p className="text-lg sm:text-xl font-bold text-amber-400 animate-pulse">
            Evento em andamento!
          </p>
        </div>
      )
    }
    return (
      <div className="text-center">
        <p className="text-lg sm:text-xl font-semibold text-stone-400">
          Evento encerrado
        </p>
      </div>
    )
  }

  const blocks = [
    { value: timeLeft.days, label: 'dias' },
    { value: timeLeft.hours, label: 'horas' },
    { value: timeLeft.minutes, label: 'min' },
    { value: timeLeft.seconds, label: 'seg' },
  ]

  return (
    <div className="flex items-center justify-center gap-3 sm:gap-4">
      {blocks.map((block) => (
        <div key={block.label} className="text-center">
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-3 sm:px-5 py-2.5 sm:py-3 min-w-[64px] sm:min-w-[80px]">
            <span className="text-2xl sm:text-3xl font-bold text-white tabular-nums">
              {String(block.value).padStart(2, '0')}
            </span>
          </div>
          <span className="text-[10px] sm:text-xs text-stone-400 uppercase tracking-wider mt-1.5 block">
            {block.label}
          </span>
        </div>
      ))}
    </div>
  )
}
